import { ButtonIcon, Typography } from '@statsbomb/kitbag-components';
import { useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { useOutlet, Link, useSearchParams, useMatch } from 'react-router-dom';
import { exportModalDataAtom } from '@/atoms/exporting';
import { EXPORT_WIDTH } from '@/consts/exporting';
import { userConfigActionAtoms } from '@/consts/scope';
import { METRIC_KEY } from '@/consts/searchKeys';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useRadarTemplateName } from '@/hooks/useRadarTemplateName';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { PromiseAtom } from '@/types/atom';
import { ExportContent } from '@/types/exporting';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { ComparisonDropdown } from '../dropdowns/ComparisonDropdown';
import RadarTemplateDropdown from '../radar/RadarTemplateDropdown';
import { RadarTemplateMenu } from '../radar/RadarTemplateMenu/RadarTemplateMenu';

export const OverviewHeader = ({
  radarTemplateConfigsAtom,
  label,
}: {
  radarTemplateConfigsAtom: PromiseAtom<UserConfigRadarTemplate[]>;
  label: string;
}) => {
  const { t } = useTranslation(['radar', 'general', 'export']);
  const outlet = useOutlet();
  const [searchParams] = useSearchParams();
  const updateQueryString = useUpdateQueryString(searchParams);
  const isTouch = useIsTouchDevice();
  const isTeamSection = useMatch('/:locale/team/*');
  const templateName = useRadarTemplateName();
  const profileLabelKey = isTeamSection ? 'teamProfileLabel' : 'positionalProfileLabel';
  const secondaryTitlePrefix = t(profileLabelKey, { ns: 'export' });

  const setExportModalData = useSetAtom(exportModalDataAtom);

  return (
    <div className="bg-canvas-tertiary-main w-full flex flex-col items-center lg:flex-row">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 px-4 py-1 w-full lg:w-auto">
        {outlet && !isTouch && (
          <Link to={`.?${updateQueryString([], [METRIC_KEY])}`}>
            <ButtonIcon icon="DoubleChevronRight" size="small" displayText="right" variant="ghost" as="span">
              {t('filters.other', { ns: 'general' })}
            </ButtonIcon>
          </Link>
        )}
        <Typography variant="headingRegular">{t('header.title')}</Typography>
        <div className="flex items-center">
          <div className="w-52">
            <RadarTemplateDropdown radarTemplateConfigsAtom={radarTemplateConfigsAtom} label={label} />
          </div>
          <ScopeProvider atoms={userConfigActionAtoms}>
            <RadarTemplateMenu />
          </ScopeProvider>
        </div>
      </div>
      <div className="h-px w-full lg:h-10 lg:w-px bg-canvas-primary-main" />
      <div className="flex-1 flex w-full items-center px-4 py-1 justify-between">
        <div className="max-w-56 flex-1">
          <ComparisonDropdown />
        </div>
        <ButtonIcon
          variant="ghost"
          onClick={() => {
            setExportModalData({
              visName: t('imageContentOptions.fileName.visTable', { ns: 'export' }),
              templateName,
              secondaryTitlePrefix,
              content: ExportContent.VIS_TABLE,
              customWidth: EXPORT_WIDTH.WIDE,
            });
          }}
          icon="Download"
        >
          {t('exportRadar', { ns: 'export' })}
        </ButtonIcon>
      </div>
    </div>
  );
};
